import { createContext, useContext, useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { authAPI } from '../services/api'

const AuthContext = createContext()

export const useAuth = () => {
  const context = useContext(AuthContext)
  if (!context) {
    throw new Error('useAuth debe ser usado dentro de un AuthProvider')
  }
  return context
}

export const AuthProvider = ({ children }) => {
  const [empleado, setEmpleado] = useState(null)
  const [empresaInfo, setEmpresaInfo] = useState(null)
  const [loading, setLoading] = useState(true)
  const navigate = useNavigate()

  useEffect(() => {
    const token = localStorage.getItem('token')
    if (token) {
      verifyToken()
    } else {
      setLoading(false)
    }
  }, [])

  // Listener para 401 desde el interceptor de axios. El interceptor solo
  // emite el evento (no hace navigate), aquí limpiamos estado y redirigimos
  // sin recargar la página. Si ya estamos en /login, ignoramos.
  useEffect(() => {
    const handleSesionCerrada = () => {
      setEmpleado(null)
      setEmpresaInfo(null)
      if (window.location.pathname !== '/login') {
        navigate('/login', { replace: true })
      }
    }
    window.addEventListener('sesion-cerrada', handleSesionCerrada)
    return () => window.removeEventListener('sesion-cerrada', handleSesionCerrada)
  }, [navigate])

  const verifyToken = async () => {
    try {
      const response = await authAPI.verify()
      setEmpleado(response.data.empleado)
      setEmpresaInfo(response.data.empresa || null)
    } catch (error) {
      localStorage.removeItem('token')
    } finally {
      setLoading(false)
    }
  }

  const login = async (email, password) => {
    const response = await authAPI.login(email, password)
    localStorage.setItem('token', response.data.token)
    sessionStorage.setItem('login_timestamp', Date.now().toString())
    setEmpleado(response.data.empleado)
    setEmpresaInfo(response.data.empresa || null)
    // Guardar empresa en localStorage para login por PIN
    if (response.data.empresa?.id) {
      localStorage.setItem('pin_empresa_id', response.data.empresa.id)
      localStorage.setItem('pin_empresa_nombre', response.data.empresa.nombre || '')
    }
    return response.data
  }

  const loginWithPin = async (pin, empresaId) => {
    const response = await authAPI.loginPin(pin, empresaId)
    localStorage.setItem('token', response.data.token)
    sessionStorage.setItem('login_timestamp', Date.now().toString())
    setEmpleado(response.data.empleado)
    setEmpresaInfo(response.data.empresa || null)
    if (response.data.empresa?.id) {
      localStorage.setItem('pin_empresa_id', response.data.empresa.id)
      localStorage.setItem('pin_empresa_nombre', response.data.empresa.nombre || '')
    }
    return response.data
  }

  const logout = async () => {
    try {
      await authAPI.logout()
    } catch (error) {
      console.error('Error en logout:', error)
    } finally {
      localStorage.removeItem('token')
      setEmpleado(null)
      setEmpresaInfo(null)
      navigate('/login', { replace: true })
    }
  }

  const changePassword = async (currentPassword, newPassword) => {
    return await authAPI.changePassword(currentPassword, newPassword)
  }

  const setAuthData = (token, empleadoData, empresaData) => {
    localStorage.setItem('token', token)
    setEmpleado(empleadoData)
    setEmpresaInfo(empresaData || null)
    if (empresaData?.id) {
      localStorage.setItem('pin_empresa_id', empresaData.id)
      localStorage.setItem('pin_empresa_nombre', empresaData.nombre || '')
    }
  }

  const refreshEmpleado = async () => {
    try {
      const response = await authAPI.verify()
      setEmpleado(response.data.empleado)
      setEmpresaInfo(response.data.empresa || null)
    } catch (error) {
      // silently ignore
    }
  }

  const value = {
    empleado,
    empresaInfo,
    empresaBloqueada: empresaInfo?.subscription_status === 'interno' ? false : (empresaInfo?.bloqueada || false),
    trialExpirado: empresaInfo?.subscription_status === 'trialing' && empresaInfo?.bloqueada,
    suscripcionActiva: ['active', 'trialing', 'interno', 'asesoria'].includes(empresaInfo?.subscription_status) && !empresaInfo?.bloqueada,
    isAsesoria: empresaInfo?.tipo === 'asesoria',
    isClienteInterno: empresaInfo?.subscription_status === 'interno',
    loading,
    login,
    loginWithPin,
    logout,
    changePassword,
    refreshEmpleado,
    setAuthData,
    isAuthenticated: !!empleado
  }

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  )
}
